/**
 * PropertyInstallModal — "Scarica l'app della casa" per la PROPERTY PAGE.
 *
 * PIVOT (2026-10): la web app installabile è la Property Page, NON il Welcome
 * Book. Questo modale la propone agli ospiti con istruzioni specifiche per
 * dispositivo e, dopo l'installazione, chiede il permesso per le notifiche.
 *
 * Regole d'apertura (decise dall'host):
 *   • con ?installa=1 (arrivo dal Welcome Book) → dopo 10s
 *   • tutti gli altri visitatori → dopo 20s
 *   • UNA sola volta per dispositivo (localStorage, per property)
 *   • MAI se l'app è già installata (display-mode standalone)
 *   • MAI sopra un altro modale aperto (prenotazione / pagamento): se c'è un
 *     [role="dialog"] aperto al momento dell'apertura, rimanda e riprova.
 *
 * Grafica: bottom-sheet nero elegante su mobile, scheda centrata 440px su
 * desktop, con il colore principale della property.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Share, Bell, Compass, Apple } from "lucide-react";
import { requestPushPermission } from "../lib/onesignal";
import {
  isIOS, isAndroid, isMobile, isSafari, isChrome,
  isIOSChrome, isEdge, isFirefox, isInAppBrowser, isMacSafari, isStandalonePWA,
} from "../lib/browserSupport";

const SEEN_KEY = (pid) => `pwa_install_promo_seen::${pid}`;

// Scheduler a livello di MODULO: il countdown non deve dipendere dal ciclo di
// vita del componente. La PropertyLanding ri-renderizza/ri-monta spesso mentre
// carica meteo/blog/traduzioni; un setTimeout dentro un useEffect verrebbe
// azzerato ad ogni remount e il modale non comparirebbe mai dopo 10/20s.
// Qui il timer vive nel modulo e, allo scadere, emette un evento DOM che il
// componente (comunque montato) intercetta.
const EVT = (pid) => `hostland-pwa-install::${pid}`;
const scheduled = {};
const fired = {};

const schedulePwaInstall = (pid) => {
  if (!pid || scheduled[pid]) return;
  let seen = false;
  try { seen = !!localStorage.getItem(SEEN_KEY(pid)); } catch {}
  if (seen || isStandalonePWA()) return;
  let installaParam = false;
  try { installaParam = new URLSearchParams(window.location.search).get("installa") === "1"; } catch {}
  const delay = installaParam ? 10000 : 20000;
  scheduled[pid] = true;
  setTimeout(() => {
    fired[pid] = true;
    try { window.dispatchEvent(new CustomEvent(EVT(pid))); } catch {}
  }, delay);
};

/* ─── Copy multilingua ─── */
const T = {
  it: {
    title: "Scarica l'app di", sub: "La casa diventa un'app sul tuo telefono",
    b1: "Prenota direttamente, senza commissioni",
    b2: "Risparmia sul tuo prossimo soggiorno",
    b3: "Scopri per primo offerte e date libere",
    install: "Installa l'app", notif: "Attiva le notifiche", later: "Non ora",
    gotIt: "Fatto, ho aggiunto l'app",
    openBrowser: "Apri nel browser", done: "Perfetto!",
    notifAsk: "Vuoi ricevere offerte e novità? Attiva le notifiche.",
    steps: {
      iosSafari: ["Tocca l'icona Condividi in basso", "Scegli \u00ab Aggiungi alla schermata Home \u00bb", "Conferma con \u00ab Aggiungi \u00bb in alto a destra"],
      iosChrome: ["Tocca l'icona Condividi di Chrome", "Scegli \u00ab Aggiungi alla schermata Home \u00bb", "Conferma con \u00ab Aggiungi \u00bb"],
      androidManual: ["Apri il menu \u22ee in alto a destra", "Scegli \u00ab Aggiungi a schermata Home \u00bb o \u00ab Installa app \u00bb", "Conferma"],
      macSafari: ["Apri il menu File in alto", "Scegli \u00ab Aggiungi al Dock \u00bb", "L'app comparirà nel Dock del Mac"],
      inapp: ["Tocca il menu in alto a destra", "Scegli \u00ab Apri nel browser \u00bb (Chrome o Safari)", "Poi riprova a installare da lì"],
      firefox: ["Firefox non installa l'app, ma puoi comunque attivare le notifiche qui sotto per ricevere offerte e novità."],
    },
  },
  en: {
    title: "Get the app for", sub: "Turn the home into an app on your phone",
    b1: "Book directly, no commissions", b2: "Save on your next stay",
    b3: "Be first to get offers and free dates",
    install: "Install the app", notif: "Enable notifications", later: "Not now",
    gotIt: "Done, I added the app", openBrowser: "Open in browser", done: "All set!",
    notifAsk: "Want offers and news? Enable notifications.",
    steps: {
      iosSafari: ["Tap the Share icon at the bottom", "Choose \u00ab Add to Home Screen \u00bb", "Confirm with \u00ab Add \u00bb top-right"],
      iosChrome: ["Tap Chrome's Share icon", "Choose \u00ab Add to Home Screen \u00bb", "Confirm with \u00ab Add \u00bb"],
      androidManual: ["Open the \u22ee menu top-right", "Choose \u00ab Add to Home screen \u00bb or \u00ab Install app \u00bb", "Confirm"],
      macSafari: ["Open the File menu", "Choose \u00ab Add to Dock \u00bb", "The app appears in your Mac Dock"],
      inapp: ["Tap the menu top-right", "Choose \u00ab Open in browser \u00bb (Chrome or Safari)", "Then try installing from there"],
      firefox: ["Firefox can't install the app, but you can still enable notifications below to get offers and news."],
    },
  },
  fr: {
    title: "Téléchargez l'app de", sub: "La maison devient une app sur ton téléphone",
    b1: "Réservez en direct, sans commissions", b2: "Économisez sur votre prochain séjour",
    b3: "Soyez le premier informé des offres et dates",
    install: "Installer l'app", notif: "Activer les notifications", later: "Plus tard",
    gotIt: "C'est fait", openBrowser: "Ouvrir dans le navigateur", done: "Parfait !",
    notifAsk: "Envie d'offres et d'actus ? Activez les notifications.",
    steps: {
      iosSafari: ["Touchez l'icône Partager en bas", "Choisissez \u00ab Sur l'écran d'accueil \u00bb", "Confirmez avec \u00ab Ajouter \u00bb"],
      iosChrome: ["Touchez l'icône Partager de Chrome", "Choisissez \u00ab Sur l'écran d'accueil \u00bb", "Confirmez avec \u00ab Ajouter \u00bb"],
      androidManual: ["Ouvrez le menu \u22ee en haut à droite", "Choisissez \u00ab Ajouter à l'écran d'accueil \u00bb", "Confirmez"],
      macSafari: ["Ouvrez le menu Fichier", "Choisissez \u00ab Ajouter au Dock \u00bb", "L'app apparaît dans le Dock"],
      inapp: ["Touchez le menu en haut à droite", "Choisissez \u00ab Ouvrir dans le navigateur \u00bb", "Réessayez ensuite d'installer"],
      firefox: ["Firefox n'installe pas l'app, mais vous pouvez activer les notifications ci-dessous."],
    },
  },
  es: {
    title: "Descarga la app de", sub: "La casa se convierte en una app en tu móvil",
    b1: "Reserva directo, sin comisiones", b2: "Ahorra en tu próxima estancia",
    b3: "Entérate primero de ofertas y fechas",
    install: "Instalar la app", notif: "Activar notificaciones", later: "Ahora no",
    gotIt: "Listo, la añadí", openBrowser: "Abrir en el navegador", done: "¡Perfecto!",
    notifAsk: "¿Quieres ofertas y novedades? Activa las notificaciones.",
    steps: {
      iosSafari: ["Toca el icono Compartir abajo", "Elige \u00ab Añadir a pantalla de inicio \u00bb", "Confirma con \u00ab Añadir \u00bb"],
      iosChrome: ["Toca el icono Compartir de Chrome", "Elige \u00ab Añadir a pantalla de inicio \u00bb", "Confirma con \u00ab Añadir \u00bb"],
      androidManual: ["Abre el menú \u22ee arriba a la derecha", "Elige \u00ab Añadir a pantalla de inicio \u00bb o \u00ab Instalar app \u00bb", "Confirma"],
      macSafari: ["Abre el menú Archivo", "Elige \u00ab Añadir al Dock \u00bb", "La app aparece en el Dock del Mac"],
      inapp: ["Toca el menú arriba a la derecha", "Elige \u00ab Abrir en el navegador \u00bb", "Luego intenta instalar desde ahí"],
      firefox: ["Firefox no instala la app, pero puedes activar las notificaciones aquí abajo."],
    },
  },
  de: {
    title: "Hol dir die App von", sub: "Das Haus wird zur App auf deinem Handy",
    b1: "Direkt buchen, ohne Provisionen", b2: "Spare bei deinem nächsten Aufenthalt",
    b3: "Erfahre Angebote und freie Termine zuerst",
    install: "App installieren", notif: "Benachrichtigungen aktivieren", later: "Später",
    gotIt: "Erledigt", openBrowser: "Im Browser öffnen", done: "Perfekt!",
    notifAsk: "Angebote und News? Aktiviere die Benachrichtigungen.",
    steps: {
      iosSafari: ["Tippe unten auf das Teilen-Symbol", "Wähle \u00ab Zum Home-Bildschirm \u00bb", "Bestätige mit \u00ab Hinzufügen \u00bb"],
      iosChrome: ["Tippe auf das Teilen-Symbol von Chrome", "Wähle \u00ab Zum Home-Bildschirm \u00bb", "Bestätige mit \u00ab Hinzufügen \u00bb"],
      androidManual: ["Öffne das \u22ee-Menü oben rechts", "Wähle \u00ab Zum Startbildschirm \u00bb oder \u00ab App installieren \u00bb", "Bestätige"],
      macSafari: ["Öffne das Menü Ablage/Datei", "Wähle \u00ab Zum Dock hinzufügen \u00bb", "Die App erscheint im Dock"],
      inapp: ["Tippe oben rechts auf das Menü", "Wähle \u00ab Im Browser öffnen \u00bb", "Dann erneut installieren"],
      firefox: ["Firefox installiert die App nicht, aber du kannst unten die Benachrichtigungen aktivieren."],
    },
  },
};

/* Determina il flusso d'installazione in base al browser / dispositivo */
const resolveFlow = (hasNativePrompt) => {
  if (isInAppBrowser()) return "inapp";
  if (isIOS()) return isIOSChrome() ? "iosChrome" : "iosSafari"; // Safari + altri WebKit iOS
  if (hasNativePrompt) return "native";                           // Chrome/Edge (Android+desktop)
  if (isAndroid()) return isFirefox() ? "firefox" : "androidManual";
  if (isMacSafari()) return "macSafari";
  if (isFirefox()) return "firefox";
  return "androidManual"; // fallback generico (desktop Chromium senza prompt catturato)
};

export default function PropertyInstallModal({ propertyId, name, iconUrl, brandColor, lang = "it" }) {
  // Lingua del modale: rispetta una scelta esplicita dell'ospite sulla pagina
  // (lang != default "it"), altrimenti usa la LINGUA DEL BROWSER, con fallback IT.
  const SUPPORTED = ["it", "en", "fr", "es", "de"];
  const browserLang = (typeof navigator !== "undefined" ? (navigator.language || "it") : "it").slice(0, 2).toLowerCase();
  const effLang = (SUPPORTED.includes(lang) && lang !== "it")
    ? lang
    : (SUPPORTED.includes(browserLang) ? browserLang : (SUPPORTED.includes(lang) ? lang : "it"));
  const L = T[effLang] || T.it;
  const acc = brandColor || "#FF385C";
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);       // notifiche attivate / completato
  const deferredRef = useRef(null);
  const [hasNativePrompt, setHasNativePrompt] = useState(false);

  // Cattura l'evento nativo d'installazione (Chrome/Edge Android+desktop)
  useEffect(() => {
    const onBIP = (e) => { e.preventDefault(); deferredRef.current = e; setHasNativePrompt(true); };
    const onInstalled = () => {
      // Dopo l'installazione: chiedi il permesso notifiche (punto 5).
      setTimeout(() => requestPushPermission(propertyId).catch(() => {}), 900);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [propertyId]);

  // Timer d'apertura (10s con ?installa=1, altrimenti 20s) — gestito a livello
  // di modulo per sopravvivere ai re-mount; qui ci limitiamo ad avviarlo e ad
  // ascoltare l'evento di scadenza.
  useEffect(() => {
    if (!propertyId) return;
    if (isStandalonePWA()) return;
    let seen = false;
    try { seen = !!localStorage.getItem(SEEN_KEY(propertyId)); } catch {}
    if (seen) return;

    const tryShow = () => {
      if (isStandalonePWA()) return;
      try { if (localStorage.getItem(SEEN_KEY(propertyId))) return; } catch {}
      // Mai sopra un altro modale aperto (prenotazione / pagamento): se c'è un
      // dialog VISIBILE, riprova tra 3s.
      const dlg = [...document.querySelectorAll('[role="dialog"]')]
        .find((d) => d.offsetParent !== null && d.getAttribute("data-testid") !== "property-install-modal");
      if (dlg) { setTimeout(tryShow, 3000); return; }
      try { localStorage.setItem(SEEN_KEY(propertyId), "1"); } catch {}
      setOpen(true);
    };

    const onFire = () => tryShow();
    window.addEventListener(EVT(propertyId), onFire);
    schedulePwaInstall(propertyId);
    // Se il timer è già scaduto prima che questo mount agganciasse il listener
    // (remount tardivo), mostra subito.
    if (fired[propertyId]) tryShow();

    return () => window.removeEventListener(EVT(propertyId), onFire);
  }, [propertyId]);

  // Scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const flow = resolveFlow(hasNativePrompt);
  const close = () => setOpen(false);

  const doNativeInstall = async () => {
    const e = deferredRef.current;
    if (!e) return;
    e.prompt();
    try {
      const { outcome } = await e.userChoice;
      if (outcome === "accepted") {
        setTimeout(() => requestPushPermission(propertyId).catch(() => {}), 900);
        close();
      }
    } catch {}
    deferredRef.current = null;
    setHasNativePrompt(false);
  };

  const doNotif = async () => {
    try { await requestPushPermission(propertyId); } catch {}
    setDone(true);
  };

  const openInBrowser = () => {
    // Prova ad aprire la stessa URL nel browser di sistema (best-effort)
    try { window.open(window.location.href, "_blank"); } catch {}
  };

  const steps = L.steps[flow];

  /* ─── Rendering CTA + istruzioni per flusso ─── */
  const renderBody = () => {
    if (done) {
      return (
        <div style={{ textAlign: "center", padding: "10px 0 4px" }}>
          <div style={{ width: 56, height: 56, borderRadius: 28, background: acc, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <Bell size={26} color="#0A0A0C" />
          </div>
          <div className="disp" style={{ fontSize: 19, fontWeight: 800 }}>{L.done}</div>
        </div>
      );
    }

    // Flusso nativo (Chrome / Edge) → bottone "Installa"
    if (flow === "native") {
      return (
        <button onClick={doNativeInstall} data-testid="pwa-install-native"
          style={btnPrimary(acc)}>
          <Download size={19} strokeWidth={2.6} /> {L.install}
        </button>
      );
    }

    // In-app browser (WhatsApp/IG/FB) → invita ad aprire nel browser
    if (flow === "inapp") {
      return (
        <>
          <StepList steps={steps} acc={acc} />
          <button onClick={openInBrowser} data-testid="pwa-install-openbrowser" style={btnPrimary(acc)}>
            <Compass size={18} strokeWidth={2.4} /> {L.openBrowser}
          </button>
        </>
      );
    }

    // Firefox → nessuna installazione, solo notifiche
    if (flow === "firefox") {
      return (
        <>
          <StepList steps={steps} acc={acc} />
          <button onClick={doNotif} data-testid="pwa-install-notif" style={btnPrimary(acc)}>
            <Bell size={18} strokeWidth={2.4} /> {L.notif}
          </button>
        </>
      );
    }

    // iOS Safari / iOS Chrome / Android manuale / Mac Safari → istruzioni passo-passo
    return (
      <>
        <StepList steps={steps} acc={acc} />
        <button onClick={doNotif} data-testid="pwa-install-gotit" style={btnPrimary(acc)}>
          {flow === "macSafari" ? (<><Apple size={18} strokeWidth={2.2} /> {L.gotIt}</>) : (<><Share size={18} strokeWidth={2.4} /> {L.gotIt}</>)}
        </button>
      </>
    );
  };

  return (
    <div data-testid="property-install-modal" role="dialog" aria-modal="true"
      onClick={close}
      style={{ position: "fixed", inset: 0, zIndex: 150, display: "flex", justifyContent: "center",
        alignItems: isMobile() ? "flex-end" : "center",
        background: "rgba(8,8,10,0.62)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}>
      <motion.div
        initial={{ y: isMobile() ? 90 : 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
        style={{ position: "relative", width: "100%", maxWidth: 440, maxHeight: "88%", overflowY: "auto",
          background: "linear-gradient(170deg,#1F1F23 0%,#0A0A0C 72%)", color: "#fff",
          borderRadius: isMobile() ? "26px 26px 0 0" : 26,
          padding: "16px 22px calc(env(safe-area-inset-bottom, 0px) + 28px)",
          boxShadow: "0 -20px 60px rgba(0,0,0,.5)", margin: isMobile() ? "0 auto" : "0 16px",
          fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
        {isMobile() && <div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(255,255,255,.25)", margin: "0 auto 16px" }} />}
        <div style={{ position: "absolute", top: -60, right: -20, width: 180, height: 180, borderRadius: "50%", background: acc, opacity: 0.25, filter: "blur(44px)", pointerEvents: "none" }} />
        <button onClick={close} data-testid="pwa-install-close" aria-label="Chiudi"
          style={{ position: "absolute", top: 12, right: 14, width: 34, height: 34, borderRadius: 17, border: "none", background: "rgba(255,255,255,.1)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 3 }}>
          <X size={18} />
        </button>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", position: "relative" }}>
          <img src={iconUrl || "/hostland-logo.png"} alt=""
            onError={(e) => { e.currentTarget.src = "/hostland-logo.png"; }}
            style={{ width: 74, height: 74, borderRadius: 18, objectFit: "cover", border: "1px solid rgba(255,255,255,.15)", marginBottom: 12 }} />
          <div className="disp" style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15, letterSpacing: "-.02em" }}>{L.title} {name}</div>
          <div style={{ fontSize: 13.5, opacity: .62, marginTop: 6 }}>{L.sub}</div>

          {!done && (
            <div style={{ width: "100%", marginTop: 16, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 20, padding: "4px 16px" }}>
              {[L.b1, L.b2, L.b3].map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderTop: i ? "1px solid rgba(255,255,255,.08)" : "none", textAlign: "left" }}>
                  <span style={{ width: 28, height: 28, borderRadius: 14, background: "rgba(255,255,255,.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: acc, fontWeight: 900 }}>✓</span>
                  <span style={{ fontSize: 14.5, color: "#fff" }}>{b}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ width: "100%", marginTop: 16 }}>{renderBody()}</div>

          {!done && (
            <button onClick={close} data-testid="pwa-install-later"
              style={{ marginTop: 12, background: "none", border: "none", color: "rgba(255,255,255,.65)", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
              {L.later}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}

const btnPrimary = (acc) => ({
  width: "100%", height: 54, borderRadius: 999, background: "#fff", color: "#0A0A0C",
  fontWeight: 800, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
  gap: 9, textDecoration: "none", border: "none", cursor: "pointer",
});

const StepList = ({ steps, acc }) => (
  <div style={{ width: "100%", marginBottom: 14, display: "flex", flexDirection: "column", gap: 10 }}>
    {steps.map((s, i) => (
      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, textAlign: "left" }}>
        {steps.length > 1 && (
          <span style={{ width: 26, height: 26, borderRadius: 13, background: acc, color: "#0A0A0C", fontWeight: 900, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>
        )}
        <span style={{ fontSize: 14, lineHeight: 1.45, color: "rgba(255,255,255,.9)", paddingTop: steps.length > 1 ? 3 : 0 }}>{s}</span>
      </div>
    ))}
  </div>
);
